// Tasks list (E7.2, R4), owned by hand: the filters, the sort and Show done live in the URL, and the loader warms the
// first page through the same queryOptions the screen reads — no waterfall. The list is prefetched, never awaited to a
// throw: a list that fails errors only its own part, and the filters and the bar stay (D193). New task opens the
// quick-create dialog (`?new`, also where /tasks/new lands; the new task opens in its detail) from the banner, beside
// Insights; for a role that cannot create it stays by the search below 600 px, locked with the reason (the reason under
// it would not read on the glacier), and from 600 in the header row's place, locked with the reason as its tooltip. From two selected tasks the pane says what is selected instead of showing one. `edit` edits the open
// task in place in the pane. Its header is the list's own (`ownHeader`), so the shell draws none above it: below 600 px its glacier banner with the search and the filters under it, from 600 one compact PageHeader row with one filter row below — the chips, a compact search, the sort (E22U6). Below 840 px, where the pane would sit under the list, a row opens the task's own screen.
// From 840 the screen fills the window below the top bar (`fill`, E22U8): the list and the open task (or the selection)
// scroll on their own in DetailSplit, the detail's width dragged between 380 and 560 and remembered; a task made with New
// task opens in the pane there, and on its own screen below 840.
import { ids } from '@orca/api-client/testIds'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Suspense, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../../../../components/ui/button'
import { cn } from '../../../../components/ui/cn'
import { DetailSplit } from '../../../../components/ui/detail-pane'
import { Icon } from '../../../../components/ui/icons'
import { LockedAction } from '../../../../components/ui/locked-action'
import { HeaderLock } from '../../../../components/ui/page-header'
import { SortMenu } from '../../../../components/ui/sort-menu'
import { membersQuery } from '../../../../features/members/queries'
import { taskListQuery, taskQuery } from '../../../../features/tasks/queries'
import { listQuery, SORTS, type TaskSort } from '../../../../features/tasks/sections'
import { TaskCreateDialog } from '../../../../features/tasks/TaskCreate'
import { type ListFilters, TaskFilterBar, TaskSearch } from '../../../../features/tasks/TaskFilters'
import { TaskList } from '../../../../features/tasks/TaskList'
import { TaskPane } from '../../../../features/tasks/TaskPane'
import { TasksBanner } from '../../../../features/tasks/TasksBanner'
import '../../../../features/tasks/strings'
import { accessQuery, can, canSomewhere } from '../../../../shared/access/access'
import type { TaskFilters } from '../../../../shared/query/keys'
import { text } from '../../../../shared/routing/search'
import { Part } from '../../../../shared/shell/StatePart'
import { useWindowClass } from '../../../../shared/shell/useWindowClass'

const STATUSES = ['todo', 'doing', 'done']
const flag = (value: unknown) => (value === true || value === 'true' || value === '1' || value === 1 ? true : undefined)

type Search = Omit<TaskFilters, 'sort' | 'dueBefore' | 'done'> & {
  task?: string
  /** The open task's fields in the pane. */
  edit?: boolean
  sort?: TaskSort
  week?: boolean
  done?: boolean
  /** The quick-create dialog, with the words and the team it starts from. */
  new?: boolean
  newTitle?: string
  newTeam?: string
}

export const Route = createFileRoute('/o/$orgSlug/tasks/')({
  staticData: { screenId: 'TasksListScreen', access: 'task.read', ownHeader: true, fill: true },
  validateSearch: (search: Record<string, unknown>): Search => {
    const status = text(search.status)
    const sort = text(search.sort)
    return {
      task: text(search.task),
      edit: flag(search.edit),
      sort: sort && (SORTS as readonly string[]).includes(sort) ? (sort as TaskSort) : undefined,
      status: status && STATUSES.includes(status) ? (status as TaskFilters['status']) : undefined,
      assignee: text(search.assignee),
      teamId: text(search.teamId),
      q: text(search.q),
      week: flag(search.week),
      done: flag(search.done),
      new: flag(search.new),
      newTitle: text(search.newTitle),
      newTeam: text(search.newTeam),
    }
  },
  loaderDeps: ({ search: { status, assignee, teamId, q, task, week, sort, done } }) => ({
    status,
    assignee,
    teamId,
    q,
    task,
    week,
    sort,
    done,
  }),
  loader: async ({ context: { queryClient, membership }, deps }) => {
    const { task, sort, done, ...filters } = deps
    const { week, ...banner } = filters
    await Promise.all([
      queryClient.prefetchInfiniteQuery(taskListQuery(membership.orgId, listQuery(filters, { sort, showDone: done }))),
      queryClient.prefetchInfiniteQuery(taskListQuery(membership.orgId, banner)),
      task ? queryClient.ensureQueryData(taskQuery(membership.orgId, task)) : undefined,
      queryClient.ensureQueryData(membersQuery(membership.orgId)),
    ])
    return { orgId: membership.orgId, orgName: membership.name, membershipId: membership.membershipId }
  },
  component: TasksListScreen,
})

function TasksListScreen() {
  const { t } = useTranslation()
  const { orgId, orgName, membershipId } = Route.useLoaderData()
  const { orgSlug } = Route.useParams()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const [selection, setSelection] = useState<{ id: string; title: string }[]>([])
  const windowClass = useWindowClass()
  const besideList = windowClass === 'expanded' || windowClass === 'wide'
  // Until the access loads there is neither the action nor the lock (fail closed, no lock flashing past).
  const { data: access } = useQuery(accessQuery(orgId))
  const canCreate = can(access, 'task.create')
  const filters: ListFilters = {
    status: search.status,
    assignee: search.assignee,
    teamId: search.teamId,
    q: search.q,
    week: search.week,
  }
  const query: TaskFilters = { status: search.status, assignee: search.assignee, teamId: search.teamId, q: search.q }
  const sort = search.sort ?? 'due'
  const setSearch = (next: Partial<Search>, replace = true) =>
    void navigate({ search: (prev) => ({ ...prev, ...next }), replace })
  const newTask = (title?: string) => setSearch({ new: true, newTitle: title, newTeam: search.teamId }, false)
  const phone = windowClass === 'compact'
  const openTask = (taskId: string) =>
    besideList
      ? setSearch({ task: taskId, edit: undefined, new: undefined, newTitle: undefined, newTeam: undefined }, false)
      : void navigate({ to: '/o/$orgSlug/tasks/$taskId', params: { orgSlug, taskId } })
  const sortMenu = (
    <SortMenu
      title={t('tasks.sort.by')}
      testId={ids.tasksList.sortButton.replace(/Button$/, '')}
      options={SORTS.map((key) => ({ value: key, label: t(`tasks.sort.${key}`) }))}
      value={sort}
      onChange={(value) => setSearch({ sort: value as TaskSort })}
      toggleLabel={t('tasks.showDone')}
      toggle={Boolean(search.done)}
      onToggle={(on) => setSearch({ done: on || undefined })}
    />
  )
  const locked = access && !canCreate && (
    <LockedAction
      label={t('tasks.new')}
      reason={t('error.forbidden.permission')}
      testId={ids.shell.lockedButton}
      className="w-auto"
    />
  )
  const filterBar = (
    <Suspense fallback={null}>
      <TaskFilterBar
        orgId={orgId}
        filters={filters}
        onChange={(next) =>
          setSearch({ status: next.status, assignee: next.assignee, teamId: next.teamId, week: next.week })
        }
      />
    </Suspense>
  )
  const list = (
    <Part testId={ids.tasksList.skeleton} orgId={orgId}>
      <TaskList
        orgId={orgId}
        orgSlug={orgSlug}
        orgName={orgName}
        filters={filters}
        sort={sort}
        showDone={Boolean(search.done)}
        openId={search.task}
        besideList={besideList}
        canCreate={canCreate}
        canInvite={canSomewhere(access, 'member.invite')}
        onOpen={(task) => openTask(task.id)}
        onSelection={(tasks) => setSelection(tasks.map(({ id, title }) => ({ id, title })))}
        onNewTask={newTask}
        onClearFilters={() => setSearch({ status: undefined, assignee: undefined, teamId: undefined, week: undefined })}
        onShowDone={() => setSearch({ done: true })}
      />
    </Part>
  )
  const detail =
    selection.length >= 2 ? (
      <SelectionCard titles={selection.map((task) => task.title)} />
    ) : (
      search.task && (
        <Part>
          <TaskPane
            key={search.task}
            orgId={orgId}
            orgSlug={orgSlug}
            taskId={search.task}
            editing={Boolean(search.edit)}
            onEditing={(on) => setSearch({ edit: on || undefined }, false)}
            onClose={() => setSearch({ task: undefined, edit: undefined }, false)}
          />
        </Part>
      )
    )
  return (
    <main
      data-testid={ids.tasksList.root}
      className={cn('flex flex-col gap-(--stack-lg)', besideList && 'min-h-0 flex-1')}
    >
      <TasksBanner
        orgId={orgId}
        orgSlug={orgSlug}
        orgName={orgName}
        filters={query}
        insights={access?.features?.insights === true}
        insightsLocked={access?.features?.insights !== true && can(access, 'plan.read')}
        newTask={
          canCreate ? (
            <Button
              variant={phone ? 'white' : 'primary'}
              size="sm"
              data-testid={ids.tasksList.newTaskButton}
              onClick={() => newTask()}
            >
              <Icon name="plus" aria-hidden className="size-4" />
              {t('tasks.new')}
            </Button>
          ) : (
            // From 600 px a role that cannot create keeps New task in its place in the row, locked (D193, E22U6).
            !phone &&
            access && (
              <HeaderLock
                label={t('tasks.new')}
                reason={t('error.forbidden.permission')}
                testId={ids.shell.lockedButton}
              />
            )
          )
        }
      >
        {!phone && (
          <div className="flex flex-wrap items-center gap-(--inline-md)">
            <div className="min-w-[20rem] flex-1">{filterBar}</div>
            <TaskSearch compact filters={filters} onChange={(next) => setSearch({ q: next.q })} />
            {sortMenu}
          </div>
        )}
      </TasksBanner>
      {phone && (
        <div className="flex flex-col gap-(--stack-md)">
          <div className="flex flex-wrap items-end gap-(--inline-md)">
            <div className="min-w-[12rem] flex-1">
              <TaskSearch filters={filters} onChange={(next) => setSearch({ q: next.q })} />
            </div>
            {sortMenu}
            {locked}
          </div>
          {filterBar}
        </div>
      )}
      {/* One tree at every width: crossing 840 keeps the open task's edit (E22U8). */}
      <DetailSplit
        label={t('core.panel.resize')}
        storageKey="orca.tasks.detailWidth"
        stacked={!besideList}
        list={list}
        detail={detail}
        detailKey={selection.length >= 2 ? 'selection' : search.task}
      />
      {search.new && canCreate && (
        <TaskCreateDialog
          orgId={orgId}
          orgSlug={orgSlug}
          orgName={orgName}
          myMembershipId={membershipId}
          title={search.newTitle}
          teamId={search.newTeam}
          onDismiss={() => setSearch({ new: undefined, newTitle: undefined, newTeam: undefined })}
          onCreated={(task) => openTask(task.id)}
        />
      )}
    </main>
  )
}

/** The pane while two or more tasks are selected: how many, which, and where to act on them. */
function SelectionCard({ titles }: { titles: string[] }) {
  const { t, i18n } = useTranslation()
  return (
    <section
      data-testid={ids.tasksList.selectionCard}
      data-count={titles.length}
      className="flex flex-col items-center gap-(--stack-sm) rounded-(--radius-card) bg-surface px-(--inset-xl) py-(--inset-xl) text-center shadow-(--shadow-card)"
    >
      <span aria-hidden className="flex size-16 items-center justify-center rounded-full bg-selected text-glacier-deep">
        <Icon name="check" className="size-7" />
      </span>
      <h2 className="font-bold text-fg text-title-2">{t('tasks.selection.title', { count: titles.length })}</h2>
      <p className="max-w-[40ch] text-callout text-fg-muted">
        {t('tasks.selection.message', {
          titles: new Intl.ListFormat(i18n.language, { type: 'conjunction' }).format(titles),
        })}
      </p>
    </section>
  )
}
